import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Package, User, Users, Search, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";

export function ChatSelector({ 
  type, // 'product', 'customer', 'representative'
  onSelect, 
  placeholder,
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const getIcon = () => {
    switch (type) {
      case 'product':
        return <Package className="h-4 w-4" />;
      case 'customer':
        return <User className="h-4 w-4" />;
      case 'representative':
        return <Users className="h-4 w-4" />;
      default:
        return <Plus className="h-4 w-4" />;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'product':
        return "Seleccionar Producto";
      case 'customer':
        return "Seleccionar Cliente";
      case 'representative':
        return "Seleccionar Representante";
      default:
        return "Seleccionar";
    }
  };

  const searchItems = async () => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      let query;
      
      switch (type) {
        case 'product':
          query = supabase
            .from('products')
            .select('code, description, price, temporada')
            .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
            .eq('active', true)
            .limit(10);
          break;
          
        case 'customer':
          query = supabase
            .from('customers')
            .select('code, name, city, province, cod_rep')
            .or(`name.ilike.%${searchTerm}%,code.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`)
            .limit(10);
          break;
          
        case 'representative':
          query = supabase
            .from('users')
            .select('cod_rep, name, role')
            .eq('role', 'vendedor')
            .or(`name.ilike.%${searchTerm}%,cod_rep.ilike.%${searchTerm}%`)
            .limit(10);
          break;
          
        default:
          return;
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error searching items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchItems();
      } else {
        setItems([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSelect = (item) => {
    onSelect(item);
    setOpen(false);
    setSearchTerm("");
    setItems([]);
  };

  const renderItem = (item) => {
    switch (type) {
      case 'product':
        return (
          <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer" onClick={() => handleSelect(item)}>
            <div>
              <p className="font-medium">{item.code}</p>
              <p className="text-sm text-gray-600">{item.description}</p>
              <p className="text-xs text-gray-500">{item.temporada} - {item.price}€</p>
            </div>
          </div>
        );
        
      case 'customer':
        return (
          <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer" onClick={() => handleSelect(item)}>
            <div>
              <p className="font-medium">{item.name}</p>
              <p className="text-sm text-gray-600">{item.code}</p>
              <p className="text-xs text-gray-500">{item.city}, {item.province}</p>
            </div>
          </div>
        );
        
      case 'representative':
        return (
          <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer" onClick={() => handleSelect(item)}>
            <div>
              <p className="font-medium">{item.name}</p>
              <p className="text-sm text-gray-600">{item.cod_rep}</p>
              <p className="text-xs text-gray-500">{item.role}</p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          {getIcon()}
          <span className="ml-2">{placeholder}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder={`Buscar ${type === 'product' ? 'producto' : type === 'customer' ? 'cliente' : 'representante'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="max-h-60 overflow-y-auto space-y-1">
            {loading && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">Buscando...</p>
              </div>
            )}
            
            {!loading && items.length === 0 && searchTerm && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No se encontraron resultados</p>
              </div>
            )}
            
            {!loading && items.map((item, index) => (
              <div key={index}>
                {renderItem(item)}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 